import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Alert, AlertTitle, CircularProgress } from '@mui/material';
import SensorNameCard from './SensorNameCard';
import PollingCard from './PollingCard';
import TimeSyncCard from './TimeSyncCard';
import CalibrateCo2Card from './CalibrateCo2Card';
import CalibrateGasCard from './CalibrateGasCard';
import WifiSetupCard from './WifiSetupCard';

/**
 * Gates the Sensor Manager "Settings" tab behind a firmware capability check.
 *
 * On mount it asks the device its settings-schema version (SETTINGS command).
 * Firmware that supports remote settings answers with a version; firmware too
 * old — or a variant without the settings subsystem — answers ERR:BADCMD
 * (getSettingsVersion resolves null) or fails to answer (rejects). In either of
 * those cases we show an "update firmware" message instead of the settings
 * cards, whose serial commands the device would otherwise reject one by one —
 * the reason opening this tab on incompatible firmware used to crash.
 */
export default function SettingsPanel({
  serialClient,
  onError = undefined,
  onNameChange = undefined,
}) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'supported' | 'unsupported'
  const [settingsVersion, setSettingsVersion] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const version = await serialClient.getSettingsVersion();
        if (cancelled) return;
        setSettingsVersion(version);
        setStatus(version != null && version >= 1 ? 'supported' : 'unsupported');
      } catch {
        // A silent or pre-settings device never answers — treat as unsupported
        // rather than letting the rejection bubble.
        if (!cancelled) setStatus('unsupported');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serialClient]);

  // WiFi provisioning (SETTINGS v2+) needs the device's serial number, which
  // isn't part of the version probe — fetch it separately once we know the
  // settings subsystem is present. Firmware that doesn't yet report a
  // serialNumber on INFO simply leaves the WiFi card hidden (see the guard
  // below) rather than crashing WifiSetupCard's required prop.
  useEffect(() => {
    if (settingsVersion == null || settingsVersion < 2) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const info = await serialClient.info();
        if (!cancelled) setDeviceInfo(info);
      } catch {
        // Leave deviceInfo null — the WiFi card just won't render.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serialClient, settingsVersion]);

  if (status === 'loading') {
    return (
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (status === 'unsupported') {
    return (
      <Alert severity="info" sx={{ mt: 3 }}>
        <AlertTitle>Settings not available</AlertTitle>
        This sensor&apos;s firmware doesn&apos;t support remote settings. Update the
        firmware from the Firmware tab to manage settings here.
      </Alert>
    );
  }

  return (
    <>
      <SensorNameCard serialClient={serialClient} onError={onError} onNameChange={onNameChange} />
      <PollingCard serialClient={serialClient} onError={onError} />
      <TimeSyncCard serialClient={serialClient} onError={onError} />
      {settingsVersion >= 2 && deviceInfo && deviceInfo.serialNumber && (
        <WifiSetupCard
          serialClient={serialClient}
          serialNumber={deviceInfo.serialNumber}
          deviceName={deviceInfo.device}
          onError={onError}
        />
      )}
      <CalibrateCo2Card serialClient={serialClient} onError={onError} />
      <CalibrateGasCard serialClient={serialClient} onError={onError} />
    </>
  );
}

SettingsPanel.propTypes = {
  serialClient: PropTypes.object.isRequired,
  onError: PropTypes.func,
  onNameChange: PropTypes.func,
};
